import { useCallback, useState } from "react";

const CARD_MIME_MAP = {
  pdf: "application/pdf",
  html: "text/html",
  htm: "text/html",
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  bmp: "image/bmp",
  tiff: "image/tiff",
  tif: "image/tiff",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xml: "text/xml",
};

/**
 * Manages document viewing state: fetches available render formats,
 * shows the format picker modal, and opens the document in a new tab.
 *
 * @param {string} token - Bearer auth token
 * @param {string} CORE_API_BASE - Base URL for the core API
 * @returns {{ formatPicker, openFormatPicker, viewDocument, viewDocumentWithFormat, closeFormatPicker }}
 */
export function useDocumentViewer(token, CORE_API_BASE) {
  // null = hidden; { item, formats, loading, error } = visible
  const [formatPicker, setFormatPicker] = useState(null);

  const openFormatPicker = useCallback(
    async (item) => {
      const rawLink = item?.itemId || item?.ItemId || item?.link || item?.Link;
      const link = rawLink;

      setFormatPicker({
        item,
        formats: null,
        loading: true,
        error: rawLink ? null : "No document link available.",
      });
      if (!rawLink) return;

      try {
        const resp = await fetch(
          `${CORE_API_BASE}/api/Document/GetDocumentRenderFormats`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ requests: [{ requestType: 0, link }] }),
          }
        );
        if (!resp.ok) {
          let msg = `HTTP ${resp.status}`;
          try {
            const j = await resp.json();
            if (j?.message) msg = j.message;
          } catch {}
          setFormatPicker({ item, formats: null, loading: false, error: msg });
          return;
        }
        const data = await resp.json();
        setFormatPicker({ item, formats: data, loading: false, error: null });
      } catch (err) {
        setFormatPicker({
          item,
          formats: null,
          loading: false,
          error: err?.message || "Failed to load formats.",
        });
      }
    },
    [CORE_API_BASE, token]
  );

  const viewDocumentWithFormat = useCallback(
    async (item, outputFormat) => {
      const rawLink = item?.itemId || item?.ItemId || item?.link || item?.Link;
      if (!rawLink) {
        setFormatPicker((prev) =>
          prev
            ? { ...prev, loading: false, error: "No document link available." }
            : null
        );
        return;
      }
      const link = rawLink;

      setFormatPicker((prev) =>
        prev ? { ...prev, loading: true, error: null } : null
      );

      try {
        const resp = await fetch(`${CORE_API_BASE}/api/Document/ViewDocument`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            requests: [
              {
                requestType: 0,
                link,
                outputFormat: outputFormat === "Native" ? null : outputFormat,
              },
            ],
          }),
        });
        if (!resp.ok) {
          let msg = `HTTP ${resp.status}`;
          try {
            const j = await resp.json();
            if (j?.message) msg = j.message;
          } catch {}
          setFormatPicker((prev) =>
            prev ? { ...prev, loading: false, error: msg } : null
          );
          return;
        }
        // Prefer the server-detected Content-Type (set from magic bytes on the backend)
        // over guessing from the requested format. This prevents a mismatch where the
        // Gatherer returns AFP bytes but the frontend labels the blob as application/pdf.
        const serverContentType = resp.headers.get("Content-Type") || "";
        const fmtLower = (outputFormat || "").toLowerCase();
        const mime =
          (serverContentType && !serverContentType.startsWith("application/octet-stream"))
            ? serverContentType.split(";")[0].trim()
            : CARD_MIME_MAP[fmtLower] ||
              CARD_MIME_MAP[
                (item?.fileExtension || item?.FileExtension || "").toLowerCase()
              ] ||
              "application/octet-stream";
        const arrayBuffer = await resp.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: mime });
        const url = URL.createObjectURL(blob);
        setFormatPicker(null);
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        setFormatPicker((prev) =>
          prev
            ? {
                ...prev,
                loading: false,
                error: err?.message || "Failed to load document.",
              }
            : null
        );
      }
    },
    [CORE_API_BASE, token]
  );

  const viewDocument = useCallback(
    (item) => openFormatPicker(item),
    [openFormatPicker]
  );

  const closeFormatPicker = useCallback(() => setFormatPicker(null), []);

  return {
    formatPicker,
    openFormatPicker,
    viewDocument,
    viewDocumentWithFormat,
    closeFormatPicker,
  };
}
